import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, CalendarOff, CalendarClock } from "lucide-react";
import {
  getMyAvailability, setWeeklyAvailability, addBlackout, removeBlackout,
} from "@/lib/availability.functions";

export const Route = createFileRoute("/_authenticated/helper/availability")({
  head: () => ({
    meta: [
      { title: "Availability · Peopels" },
      { name: "description", content: "Set your weekly working hours and block dates so customers can book you instantly." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AvailabilityPage,
});

type Rule = { day_of_week: number; start_minute: number; end_minute: number };
type Blackout = { id: string; starts_at: string; ends_at: string; reason: string | null };

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function toHHMM(min: number) {
  const h = Math.floor(min / 60).toString().padStart(2, "0");
  const m = (min % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}
function fromHHMM(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const h = Number(m[1]); const mm = Number(m[2]);
  if (h < 0 || h > 24 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
}

function AvailabilityPage() {
  const load = useServerFn(getMyAvailability);
  const save = useServerFn(setWeeklyAvailability);
  const addBO = useServerFn(addBlackout);
  const rmBO = useServerFn(removeBlackout);

  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState<Rule[]>([]);
  const [blackouts, setBlackouts] = useState<Blackout[]>([]);
  const [saving, setSaving] = useState(false);
  const [boStart, setBoStart] = useState("");
  const [boEnd, setBoEnd] = useState("");
  const [boReason, setBoReason] = useState("");
  const [boSaving, setBoSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await load();
        setRules(res.rules.map((r) => ({
          day_of_week: r.day_of_week, start_minute: r.start_minute, end_minute: r.end_minute,
        })));
        setBlackouts(res.blackouts as Blackout[]);
      } catch (e) { toast.error((e as Error).message); }
      finally { setLoading(false); }
    })();
  }, [load]);

  function addRule(day: number) {
    setRules((prev) => [...prev, { day_of_week: day, start_minute: 9 * 60, end_minute: 17 * 60 }]);
  }
  function updateRule(i: number, patch: Partial<Rule>) {
    setRules((prev) => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  }
  function removeRule(i: number) {
    setRules((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function handleSave() {
    for (const r of rules) {
      if (r.end_minute <= r.start_minute) { toast.error(`${DAYS[r.day_of_week]}: end must be after start`); return; }
    }
    setSaving(true);
    try {
      await save({ data: { rules } });
      toast.success("Availability saved");
    } catch (e) { toast.error((e as Error).message); }
    finally { setSaving(false); }
  }

  async function handleAddBlackout() {
    if (!boStart || !boEnd) { toast.error("Pick start and end"); return; }
    const s = new Date(boStart); const e = new Date(boEnd);
    if (!(e.getTime() > s.getTime())) { toast.error("End must be after start"); return; }
    setBoSaving(true);
    try {
      const res = await addBO({ data: {
        starts_at: s.toISOString(), ends_at: e.toISOString(),
        reason: boReason.trim() || undefined,
      }});
      setBlackouts((prev) => [...prev, res.blackout as Blackout].sort((a, b) =>
        a.starts_at.localeCompare(b.starts_at)));
      setBoStart(""); setBoEnd(""); setBoReason("");
      toast.success("Block added");
    } catch (e) { toast.error((e as Error).message); }
    finally { setBoSaving(false); }
  }

  async function handleRemoveBlackout(id: string) {
    try {
      await rmBO({ data: { id } });
      setBlackouts((prev) => prev.filter((b) => b.id !== id));
    } catch (e) { toast.error((e as Error).message); }
  }

  const grouped: Rule[][] = Array.from({ length: 7 }, () => []);
  rules.forEach((r) => grouped[r.day_of_week].push(r));

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-3xl flex-1 px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
        <header className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight">Availability</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Set your weekly hours (Europe/London) and add one-off blocks. Customers can instant-book any open 30-minute slot.
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            <Link to="/helper/onboard" className="underline">Make sure Instant Booking is enabled</Link> in your profile.
          </p>
        </header>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <>
            <section className="rounded-2xl border border-border bg-card p-5 sm:p-6">
              <div className="mb-4 flex items-center gap-2 text-base font-medium">
                <CalendarClock className="h-4 w-4" /> Weekly hours
              </div>
              <div className="space-y-4">
                {grouped.map((dayRules, day) => (
                  <div key={day} className="grid gap-2 sm:grid-cols-[80px_1fr]">
                    <div className="pt-2 text-sm font-medium">{DAYS[day]}</div>
                    <div className="space-y-2">
                      {dayRules.length === 0 ? (
                        <Button type="button" variant="outline" size="sm" onClick={() => addRule(day)}>
                          <Plus className="mr-1 h-3.5 w-3.5" /> Add hours
                        </Button>
                      ) : (
                        <>
                          {dayRules.map((r) => {
                            const i = rules.indexOf(r);
                            return (
                              <div key={i} className="flex flex-wrap items-center gap-2">
                                <Input
                                  type="time" value={toHHMM(r.start_minute)}
                                  onChange={(e) => {
                                    const v = fromHHMM(e.target.value);
                                    if (v !== null) updateRule(i, { start_minute: v });
                                  }}
                                  className="w-28" aria-label={`${DAYS[day]} start`}
                                />
                                <span className="text-muted-foreground">→</span>
                                <Input
                                  type="time" value={toHHMM(r.end_minute)}
                                  onChange={(e) => {
                                    const v = fromHHMM(e.target.value);
                                    if (v !== null) updateRule(i, { end_minute: v });
                                  }}
                                  className="w-28" aria-label={`${DAYS[day]} end`}
                                />
                                <Button type="button" variant="ghost" size="icon"
                                  onClick={() => removeRule(i)} aria-label="Remove">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            );
                          })}
                          <Button type="button" variant="ghost" size="sm" onClick={() => addRule(day)}>
                            <Plus className="mr-1 h-3.5 w-3.5" /> Add another window
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <Button onClick={handleSave} disabled={saving}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save weekly hours
                </Button>
              </div>
            </section>

            <section className="mt-8 rounded-2xl border border-border bg-card p-5 sm:p-6">
              <div className="mb-4 flex items-center gap-2 text-base font-medium">
                <CalendarOff className="h-4 w-4" /> Time off / blocks
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="bo-start">Start</Label>
                  <Input id="bo-start" type="datetime-local" value={boStart} onChange={(e) => setBoStart(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="bo-end">End</Label>
                  <Input id="bo-end" type="datetime-local" value={boEnd} onChange={(e) => setBoEnd(e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="bo-reason">Reason (optional)</Label>
                  <Input id="bo-reason" maxLength={200} value={boReason}
                    onChange={(e) => setBoReason(e.target.value)} placeholder="Holiday, training, etc." />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button onClick={handleAddBlackout} disabled={boSaving}>
                  {boSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Add block
                </Button>
              </div>

              <ul className="mt-6 divide-y divide-border">
                {blackouts.length === 0 && (
                  <li className="py-4 text-sm text-muted-foreground">No upcoming blocks.</li>
                )}
                {blackouts.map((b) => (
                  <li key={b.id} className="flex items-center justify-between py-3">
                    <div className="text-sm">
                      <div className="font-medium">
                        {new Date(b.starts_at).toLocaleString("en-GB", { timeZone: "Europe/London" })}
                        {" → "}
                        {new Date(b.ends_at).toLocaleString("en-GB", { timeZone: "Europe/London" })}
                      </div>
                      {b.reason && <div className="text-muted-foreground">{b.reason}</div>}
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => handleRemoveBlackout(b.id)} aria-label="Remove block">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
